import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ModalShell from '../../Attendance/Component/ModalShell';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    endShiftAssignment,
    createShiftAssignment,
} from '../../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import {
    WEEKDAYS,
    toBool,
    todayISO,
    addDays,
    formatDateOnly,
    shiftTimeRange,
} from '../../../../consts/shiftConstants';

// Move an employee from one shift to another.
//
// There is no single endpoint for this, and there shouldn't be: patching
// shift_id on a live assignment would make the past claim the employee always
// worked the new shift. The supported flow is end-then-create, and this dialog
// runs both calls so the admin sees it as one decision.
//
//   1. POST /shift-assignments/:id/end  { effective_to: <change date - 1> }
//   2. POST /shift-assignments          { effective_from: <change date> }
//
// The employee form does the same thing in one request (action: CHANGED). This
// dialog exists for the cases that form refuses — a future-dated move, or a
// change made from the assignments screen rather than the employee record.

const ChangeShiftModal = ({ assignment, onClose, onDone }) => {
    const dispatch = useDispatch();
    const { assignmentMutationLoading, shifts, shiftEnums } = useSelector(state => state.ShiftReducer || {});

    const [changeDate, setChangeDate] = useState(todayISO());
    const [newShiftId, setNewShiftId] = useState('');
    const [weeklyOffDays, setWeeklyOffDays] = useState([]);
    const [errors, setErrors] = useState({});
    // Step 1 succeeded but step 2 failed — the old assignment is already
    // closed, so the retry must not run the end call again.
    const [endedAlready, setEndedAlready] = useState(false);

    useEffect(() => {
        if (!assignment) return;
        setChangeDate(todayISO());
        setNewShiftId('');
        setWeeklyOffDays(Array.isArray(assignment.weekly_off_days) ? assignment.weekly_off_days : []);
        setErrors({});
        setEndedAlready(false);
    }, [assignment]);

    const shiftOptions = useMemo(() => (
        (shifts || [])
            .filter(s => toBool(s.is_active) && String(s.shift_id) !== String(assignment?.shift_id))
            .map(s => ({
                value: String(s.shift_id),
                label: `${s.shift_code} · ${s.shift_name} (${shiftTimeRange(s)})`,
            }))
    ), [shifts, assignment]);

    const weekdays = shiftEnums?.weekdays?.length
        ? shiftEnums.weekdays.map(w => ({
            ...w,
            short: WEEKDAYS.find(d => d.value === w.value)?.short || w.name?.slice(0, 3),
        }))
        : WEEKDAYS;

    if (!assignment) return null;

    const employeeName = [assignment.employee?.first_name, assignment.employee?.last_name]
        .filter(Boolean).join(' ').trim() || `Employee #${assignment.employee_id}`;

    const previousLastDay = changeDate ? addDays(changeDate, -1) : null;

    const toggleOffDay = (value) => {
        setWeeklyOffDays(prev => (prev.includes(value)
            ? prev.filter(d => d !== value)
            : [...prev, value].sort((a, b) => a - b)));
        setErrors(prev => ({ ...prev, weekly_off_days: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!newShiftId || newShiftId === 'all') e.shift_id = 'Pick the new shift';
        if (!changeDate) e.changeDate = 'Required';
        else if (previousLastDay && previousLastDay < assignment.effective_from) {
            e.changeDate = `Must be after the current assignment started (${formatDateOnly(assignment.effective_from)})`;
        }
        if (weeklyOffDays.length >= 7) e.weekly_off_days = 'At least one working day is required';
        return e;
    };

    const createNewAssignment = () => {
        dispatch(createShiftAssignment({
            employee_id: assignment.employee_id,
            shift_id: Number(newShiftId),
            effective_from: changeDate,
            effective_to: null,
            weekly_off_days: weeklyOffDays,
            onSuccess: (created) => { onClose(); onDone?.(created); },
            onError: () => {
                // The old assignment is closed but the new one never landed —
                // say so plainly rather than leaving the employee unassigned
                // without explanation.
                toast.error('The previous assignment was ended but the new one could not be created. Fix the dates and try again.');
            },
        }));
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        if (endedAlready) {
            createNewAssignment();
            return;
        }
        dispatch(endShiftAssignment({
            assignment_id: assignment.assignment_id,
            effective_to: previousLastDay,
            onSuccess: () => {
                setEndedAlready(true);
                createNewAssignment();
            },
        }));
    };

    const inputClass = (field) =>
        `tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${errors[field] ? 'tw-border-red-300 focus:tw-ring-red-400' : 'tw-border-gray-200 focus:tw-ring-blue-500'}`;

    return (
        <ModalShell
            isOpen={Boolean(assignment)}
            title="Change shift"
            icon="mdi:swap-horizontal"
            onClose={onClose}
            busy={assignmentMutationLoading}
            maxWidth="tw-max-w-lg"
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={assignmentMutationLoading}
                    className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-blue-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                    {assignmentMutationLoading ? 'Working…' : endedAlready ? 'Retry new assignment' : 'Change shift'}
                </button>
            }
        >
            <p className="tw-text-sm tw-text-gray-600 tw-mb-3">
                Move <strong>{employeeName}</strong> off the{' '}
                <strong>{assignment.shift?.shift_name || 'current'}</strong> shift.
            </p>

            <div className="tw-space-y-3">
                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">New shift starts on *</label>
                    <input
                        type="date"
                        value={changeDate}
                        onChange={(e) => { setChangeDate(e.target.value); setErrors(p => ({ ...p, changeDate: undefined })); }}
                        className={inputClass('changeDate')}
                    />
                    {errors.changeDate
                        ? <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{errors.changeDate}</p>
                        : previousLastDay && (
                            <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-mb-0">
                                The current assignment will end on {formatDateOnly(previousLastDay)}.
                            </p>
                        )}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">New shift *</label>
                    <SearchableDropdown
                        value={newShiftId}
                        onChange={(v) => { setNewShiftId(v === 'all' ? '' : v); setErrors(p => ({ ...p, shift_id: undefined })); }}
                        options={shiftOptions}
                        placeholder="Select a shift"
                    />
                    {errors.shift_id && <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{errors.shift_id}</p>}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Weekly offs on the new shift</label>
                    <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                        {weekdays.map(d => {
                            const selected = weeklyOffDays.includes(d.value);
                            return (
                                <button
                                    key={d.value}
                                    type="button"
                                    onClick={() => toggleOffDay(d.value)}
                                    className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-border ${selected
                                        ? 'tw-bg-gray-700 tw-text-white tw-border-gray-700'
                                        : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'}`}
                                >
                                    {d.short}
                                </button>
                            );
                        })}
                    </div>
                    {errors.weekly_off_days && <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{errors.weekly_off_days}</p>}
                </div>

                {endedAlready && (
                    <p className="tw-text-[11px] tw-text-amber-800 tw-bg-amber-50 tw-rounded-md tw-px-3 tw-py-2 tw-mb-0 tw-flex tw-items-start tw-gap-1.5">
                        <Icon icon="mdi:alert-outline" className="tw-mt-0.5 tw-flex-shrink-0" />
                        The old assignment has already been ended. Only the new assignment still needs to be created.
                    </p>
                )}
            </div>
        </ModalShell>
    );
};

export default ChangeShiftModal;
